import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePark } from '../contexts/ParkContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Users,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Eye,
  Building,
  Search,
  Filter,
  DollarSign,
  Download,
  ExternalLink,
  Loader2,
  History
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUPPLIER_STATUS_CONFIG = {
  pendiente: { label: 'Pendiente', color: 'bg-amber-100 text-amber-800', icon: Clock },
  aprobado: { label: 'Aprobado', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  rechazado: { label: 'Rechazado', color: 'bg-red-100 text-red-800', icon: XCircle },
  suspendido: { label: 'Suspendido', color: 'bg-slate-100 text-slate-800', icon: AlertCircle }
};

const INVOICE_STATUS_CONFIG = {
  recibida: { label: 'Recibida', color: 'bg-blue-100 text-blue-800' },
  en_revision: { label: 'En Revisión', color: 'bg-yellow-100 text-yellow-800' },
  autorizada: { label: 'Autorizada', color: 'bg-green-100 text-green-800' },
  rechazada: { label: 'Rechazada', color: 'bg-red-100 text-red-800' },
  pagada: { label: 'Pagada', color: 'bg-emerald-100 text-emerald-800' }
};

const Proveedores = () => {
  const { user } = useAuth();
  const { parks } = usePark();
  const [activeTab, setActiveTab] = useState('suppliers');
  const [suppliers, setSuppliers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [parkFilter, setParkFilter] = useState('all');
  
  const [showSupplierDetail, setShowSupplierDetail] = useState(null);
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(null);
  const [showAssignParks, setShowAssignParks] = useState(null);
  const [showLogs, setShowLogs] = useState(null);
  
  const [approvalData, setApprovalData] = useState({ approved: false, comentarios: '', park_ids: [] });
  const [selectedParks, setSelectedParks] = useState([]);
  const [invoiceStatusUpdate, setInvoiceStatusUpdate] = useState({ status: '', comentarios: '' });
  const [supplierLogs, setSupplierLogs] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/suppliers`, { headers: getAuthHeaders() });
      setSuppliers(res.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  }, [getAuthHeaders]);

  const fetchInvoices = useCallback(async () => {
    try {
      const params = {};
      if (parkFilter !== 'all') params.park_id = parkFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const res = await axios.get(`${API_URL}/api/invoices`, { 
        headers: getAuthHeaders(),
        params 
      });
      setInvoices(res.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  }, [getAuthHeaders, parkFilter, statusFilter]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchSuppliers(), fetchInvoices()]);
    setLoading(false);
  }, [fetchSuppliers, fetchInvoices]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'invoices') {
      fetchInvoices();
    }
  }, [activeTab, parkFilter, statusFilter, fetchInvoices]);

  const handleApproveSupplier = async () => {
    if (!showApprovalDialog) return;
    
    setSubmitting(true);
    try {
      await axios.put(
        `${API_URL}/api/suppliers/${showApprovalDialog.id}/approve`,
        {
          approved: approvalData.approved,
          comentarios: approvalData.comentarios,
          park_ids: approvalData.approved ? approvalData.park_ids : []
        },
        { headers: getAuthHeaders() }
      );
      toast.success(approvalData.approved ? 'Proveedor aprobado' : 'Proveedor rechazado');
      setShowApprovalDialog(null);
      setApprovalData({ approved: false, comentarios: '', park_ids: [] });
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignParks = async () => {
    if (!showAssignParks) return;
    
    setSubmitting(true);
    try {
      await axios.put(
        `${API_URL}/api/suppliers/${showAssignParks.id}/assign-parks`,
        selectedParks,
        { headers: getAuthHeaders() }
      );
      toast.success('Parques asignados exitosamente');
      setShowAssignParks(null);
      setSelectedParks([]);
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar parques');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateInvoiceStatus = async () => {
    if (!showInvoiceDetail) return;
    
    setSubmitting(true);
    try {
      await axios.put(
        `${API_URL}/api/invoices/${showInvoiceDetail.id}/status`,
        invoiceStatusUpdate,
        { headers: getAuthHeaders() }
      );
      toast.success('Estado de factura actualizado');
      setShowInvoiceDetail(null);
      setInvoiceStatusUpdate({ status: '', comentarios: '' });
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSubmitting(false);
    }
  };

  const fetchSupplierLogs = async (supplierId) => {
    try {
      const res = await axios.get(`${API_URL}/api/suppliers/${supplierId}/logs`, {
        headers: getAuthHeaders()
      });
      setSupplierLogs(res.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-MX');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-MX');
  };

  const filteredSuppliers = suppliers.filter(s => {
    const matchesSearch = s.razon_social?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          s.rfc?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const pendingCount = suppliers.filter(s => s.status === 'pendiente').length;
  const pendingInvoicesCount = invoices.filter(i => i.status === 'recibida').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="page-title">
            Gestión de Proveedores
          </h1>
          <p className="text-slate-500">Administre proveedores y facturas</p>
        </div>
        
        {/* Summary Cards */}
        <div className="flex gap-4">
          {pendingCount > 0 && (
            <div className="bg-amber-50 border border-amber-200 px-4 py-2 rounded flex items-center gap-2">
              <Clock className="text-amber-600" size={18} />
              <span className="text-amber-800 font-medium">{pendingCount} pendientes</span>
            </div>
          )}
          {pendingInvoicesCount > 0 && (
            <div className="bg-blue-50 border border-blue-200 px-4 py-2 rounded flex items-center gap-2">
              <FileText className="text-blue-600" size={18} />
              <span className="text-blue-800 font-medium">{pendingInvoicesCount} facturas por revisar</span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="suppliers" className="flex items-center gap-2" data-testid="tab-suppliers">
            <Users size={16} />
            Proveedores
          </TabsTrigger>
          <TabsTrigger value="invoices" className="flex items-center gap-2" data-testid="tab-invoices">
            <FileText size={16} />
            Facturas
          </TabsTrigger>
        </TabsList>

        {/* Suppliers Tab */}
        <TabsContent value="suppliers" className="mt-6">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  placeholder="Buscar por razón social o RFC..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-suppliers"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="filter-status">
                <Filter size={16} className="mr-2" />
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="pendiente">Pendiente</SelectItem>
                <SelectItem value="aprobado">Aprobado</SelectItem>
                <SelectItem value="rechazado">Rechazado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Suppliers Table */}
          {filteredSuppliers.length === 0 ? (
            <div className="bg-white shadow-sm p-12 text-center">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No hay proveedores registrados</p>
            </div>
          ) : (
            <div className="bg-white shadow-sm overflow-hidden">
              <table className="w-full" data-testid="suppliers-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Proveedor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">RFC</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Contacto</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Giro</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Parques</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Estado</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredSuppliers.map((supplier) => {
                    const statusConfig = SUPPLIER_STATUS_CONFIG[supplier.status] || SUPPLIER_STATUS_CONFIG.pendiente;
                    const StatusIcon = statusConfig.icon;
                    return (
                      <tr key={supplier.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-900">{supplier.razon_social}</p>
                          <p className="text-xs text-slate-500">{supplier.email}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 font-mono">
                          {supplier.rfc}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-slate-900">{supplier.nombre_contacto}</p>
                          <p className="text-xs text-slate-500">{supplier.telefono}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {supplier.giro}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-sm text-slate-600">
                            {supplier.park_ids?.length || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${statusConfig.color}`}>
                            <StatusIcon size={12} className="mr-1" />
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowSupplierDetail(supplier)}
                              title="Ver detalle"
                              data-testid={`btn-view-supplier-${supplier.id}`}
                            >
                              <Eye size={16} />
                            </Button>
                            {supplier.status === 'pendiente' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setShowApprovalDialog(supplier);
                                  setApprovalData({ approved: false, comentarios: '', park_ids: [] });
                                }}
                                title="Revisar"
                                className="text-amber-600"
                              >
                                <CheckCircle size={16} />
                              </Button>
                            )}
                            {supplier.status === 'aprobado' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setShowAssignParks(supplier);
                                  setSelectedParks(supplier.park_ids || []);
                                }}
                                title="Asignar parques"
                                className="text-blue-600"
                              >
                                <Building size={16} />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setShowLogs(supplier);
                                fetchSupplierLogs(supplier.id);
                              }}
                              title="Ver historial"
                            >
                              <History size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="mt-6">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <Select value={parkFilter} onValueChange={setParkFilter}>
              <SelectTrigger className="w-[200px]" data-testid="filter-park">
                <Building size={16} className="mr-2" />
                <SelectValue placeholder="Parque" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los parques</SelectItem>
                {parks.map(park => (
                  <SelectItem key={park.id} value={park.id}>
                    {park.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="filter-invoice-status">
                <Filter size={16} className="mr-2" />
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="recibida">Recibida</SelectItem>
                <SelectItem value="en_revision">En Revisión</SelectItem>
                <SelectItem value="autorizada">Autorizada</SelectItem>
                <SelectItem value="rechazada">Rechazada</SelectItem>
                <SelectItem value="pagada">Pagada</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Invoices Table */}
          {invoices.length === 0 ? (
            <div className="bg-white shadow-sm p-12 text-center">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No hay facturas registradas</p>
            </div>
          ) : (
            <div className="bg-white shadow-sm overflow-hidden">
              <table className="w-full" data-testid="invoices-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Folio</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Proveedor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Parque</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Fecha</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Archivos</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Estado</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {invoices.map((invoice) => {
                    const statusConfig = INVOICE_STATUS_CONFIG[invoice.status] || INVOICE_STATUS_CONFIG.recibida;
                    return (
                      <tr key={invoice.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-900">
                          {invoice.numero_factura}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-slate-900">{invoice.razon_social_proveedor}</p>
                          <p className="text-xs text-slate-500">{invoice.rfc_proveedor}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {invoice.park_nombre}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {formatDate(invoice.fecha_factura)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-900 text-right font-medium">
                          {formatCurrency(invoice.monto_total)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {invoice.pdf_url && (
                              <a
                                href={invoice.pdf_url}
                                download
                                className="w-6 h-6 rounded bg-red-100 text-red-600 flex items-center justify-center text-xs hover:bg-red-200"
                                title="Descargar PDF"
                              >
                                P
                              </a>
                            )}
                            {invoice.xml_url && (
                              <a
                                href={invoice.xml_url}
                                download
                                className="w-6 h-6 rounded bg-green-100 text-green-600 flex items-center justify-center text-xs hover:bg-green-200"
                                title="Descargar XML"
                              >
                                X
                              </a>
                            )}
                            {!invoice.pdf_url && !invoice.xml_url && (
                              <span className="text-xs text-slate-400">Sin archivos</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${statusConfig.color}`}>
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setShowInvoiceDetail(invoice);
                              setInvoiceStatusUpdate({ status: invoice.status, comentarios: '' });
                            }}
                            data-testid={`btn-view-invoice-${invoice.id}`}
                          >
                            <Eye size={16} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Supplier Detail Dialog */}
      <Dialog open={!!showSupplierDetail} onOpenChange={() => setShowSupplierDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalle del Proveedor</DialogTitle>
          </DialogHeader>
          {showSupplierDetail && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">{showSupplierDetail.razon_social}</h3>
                  <p className="text-slate-500">{showSupplierDetail.email}</p>
                </div>
                <span className={`inline-flex items-center px-3 py-1 rounded text-sm font-medium ${
                  SUPPLIER_STATUS_CONFIG[showSupplierDetail.status]?.color
                }`}>
                  {SUPPLIER_STATUS_CONFIG[showSupplierDetail.status]?.label}
                </span>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-3">
                  <h4 className="font-semibold text-slate-700 border-b pb-1">Datos Fiscales</h4>
                  <p><span className="text-slate-500">RFC:</span> <span className="font-mono">{showSupplierDetail.rfc}</span></p>
                  <p><span className="text-slate-500">Régimen:</span> {showSupplierDetail.regimen_fiscal}</p>
                  <p><span className="text-slate-500">Giro:</span> {showSupplierDetail.giro}</p>
                </div>
                <div className="space-y-3">
                  <h4 className="font-semibold text-slate-700 border-b pb-1">Contacto</h4>
                  <p><span className="text-slate-500">Nombre:</span> {showSupplierDetail.nombre_contacto}</p>
                  <p><span className="text-slate-500">Teléfono:</span> {showSupplierDetail.telefono}</p>
                  <p><span className="text-slate-500">Email:</span> {showSupplierDetail.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-3">
                  <h4 className="font-semibold text-slate-700 border-b pb-1">Dirección</h4>
                  <p>{showSupplierDetail.direccion}</p>
                  <p>{showSupplierDetail.ciudad}, {showSupplierDetail.estado} C.P. {showSupplierDetail.codigo_postal}</p>
                </div>
                <div className="space-y-3">
                  <h4 className="font-semibold text-slate-700 border-b pb-1">Datos Bancarios</h4>
                  <p><span className="text-slate-500">Banco:</span> {showSupplierDetail.banco}</p>
                  <p><span className="text-slate-500">Cuenta:</span> {showSupplierDetail.cuenta_bancaria}</p>
                  <p><span className="text-slate-500">CLABE:</span> <span className="font-mono text-xs">{showSupplierDetail.clabe}</span></p>
                </div>
              </div>

              {/* Documents */}
              <div>
                <h4 className="font-semibold text-slate-700 border-b pb-1 mb-3">Documentos</h4>
                <div className="flex gap-4">
                  {showSupplierDetail.doc_constancia_fiscal && (
                    <a href={showSupplierDetail.doc_constancia_fiscal} download className="flex items-center gap-2 text-blue-600 hover:underline text-sm">
                      <Download size={14} />
                      Constancia Fiscal
                    </a>
                  )}
                  {showSupplierDetail.doc_identificacion && (
                    <a href={showSupplierDetail.doc_identificacion} download className="flex items-center gap-2 text-blue-600 hover:underline text-sm">
                      <Download size={14} />
                      Identificación
                    </a>
                  )}
                  {showSupplierDetail.doc_comprobante_domicilio && (
                    <a href={showSupplierDetail.doc_comprobante_domicilio} download className="flex items-center gap-2 text-blue-600 hover:underline text-sm">
                      <Download size={14} />
                      Comprobante Domicilio
                    </a>
                  )}
                  {!showSupplierDetail.doc_constancia_fiscal && !showSupplierDetail.doc_identificacion && !showSupplierDetail.doc_comprobante_domicilio && (
                    <p className="text-slate-400 text-sm">No hay documentos cargados</p>
                  )}
                </div>
              </div>

              {/* Assigned Parks */}
              {showSupplierDetail.park_ids?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-700 border-b pb-1 mb-3">Parques Asignados</h4>
                  <div className="flex flex-wrap gap-2">
                    {showSupplierDetail.park_ids.map(parkId => {
                      const park = parks.find(p => p.id === parkId);
                      return (
                        <span key={parkId} className="px-2 py-1 bg-slate-100 rounded text-sm">
                          {park?.nombre || parkId}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="text-xs text-slate-400">
                Registrado: {formatDateTime(showSupplierDetail.created_at)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={!!showApprovalDialog} onOpenChange={() => setShowApprovalDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Revisar Proveedor</DialogTitle>
          </DialogHeader>
          {showApprovalDialog && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded">
                <p className="font-medium">{showApprovalDialog.razon_social}</p>
                <p className="text-sm text-slate-500">RFC: {showApprovalDialog.rfc}</p>
              </div>

              <div>
                <Label>Decisión</Label>
                <div className="flex gap-4 mt-2">
                  <Button
                    type="button"
                    variant={approvalData.approved ? 'default' : 'outline'}
                    className={approvalData.approved ? 'bg-green-600 hover:bg-green-700' : ''}
                    onClick={() => setApprovalData(prev => ({ ...prev, approved: true }))}
                  >
                    <CheckCircle size={16} className="mr-2" />
                    Aprobar
                  </Button>
                  <Button
                    type="button"
                    variant={!approvalData.approved ? 'destructive' : 'outline'}
                    onClick={() => setApprovalData(prev => ({ ...prev, approved: false }))}
                  >
                    <XCircle size={16} className="mr-2" />
                    Rechazar
                  </Button>
                </div>
              </div>

              {approvalData.approved && (
                <div>
                  <Label>Asignar Parques</Label>
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border rounded p-2">
                    {parks.map(park => (
                      <div key={park.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`park-${park.id}`}
                          checked={approvalData.park_ids.includes(park.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setApprovalData(prev => ({
                                ...prev,
                                park_ids: [...prev.park_ids, park.id]
                              }));
                            } else {
                              setApprovalData(prev => ({
                                ...prev,
                                park_ids: prev.park_ids.filter(id => id !== park.id)
                              }));
                            }
                          }}
                        />
                        <label htmlFor={`park-${park.id}`} className="text-sm">
                          {park.nombre}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label>Comentarios {!approvalData.approved && '*'}</Label>
                <Textarea
                  value={approvalData.comentarios}
                  onChange={(e) => setApprovalData(prev => ({ ...prev, comentarios: e.target.value }))}
                  placeholder={approvalData.approved ? 'Comentarios opcionales...' : 'Motivo del rechazo...'}
                  required={!approvalData.approved}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowApprovalDialog(null)}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleApproveSupplier}
                  disabled={submitting || (!approvalData.approved && !approvalData.comentarios)}
                  className={approvalData.approved ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                >
                  {submitting && <Loader2 className="animate-spin mr-2" size={16} />}
                  Confirmar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Parks Dialog */}
      <Dialog open={!!showAssignParks} onOpenChange={() => setShowAssignParks(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Asignar Parques</DialogTitle>
          </DialogHeader>
          {showAssignParks && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded">
                <p className="font-medium">{showAssignParks.razon_social}</p>
              </div>

              <div>
                <Label>Seleccione los parques donde puede facturar:</Label>
                <div className="mt-2 space-y-2 max-h-60 overflow-y-auto border rounded p-3">
                  {parks.map(park => (
                    <div key={park.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`assign-park-${park.id}`}
                        checked={selectedParks.includes(park.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedParks(prev => [...prev, park.id]);
                          } else {
                            setSelectedParks(prev => prev.filter(id => id !== park.id));
                          }
                        }}
                      />
                      <label htmlFor={`assign-park-${park.id}`} className="text-sm">
                        {park.nombre}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAssignParks(null)}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleAssignParks}
                  disabled={submitting}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  {submitting && <Loader2 className="animate-spin mr-2" size={16} />}
                  Guardar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invoice Detail Dialog */}
      <Dialog open={!!showInvoiceDetail} onOpenChange={() => setShowInvoiceDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalle de Factura</DialogTitle>
          </DialogHeader>
          {showInvoiceDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Folio:</span>
                  <p className="font-medium">{showInvoiceDetail.numero_factura}</p>
                </div>
                <div>
                  <span className="text-slate-500">Fecha:</span>
                  <p className="font-medium">{formatDate(showInvoiceDetail.fecha_factura)}</p>
                </div>
                <div>
                  <span className="text-slate-500">Proveedor:</span>
                  <p className="font-medium">{showInvoiceDetail.razon_social_proveedor}</p>
                </div>
                <div>
                  <span className="text-slate-500">RFC:</span>
                  <p className="font-medium font-mono">{showInvoiceDetail.rfc_proveedor}</p>
                </div>
                <div>
                  <span className="text-slate-500">Parque:</span>
                  <p className="font-medium">{showInvoiceDetail.park_nombre}</p>
                </div>
                <div>
                  <span className="text-slate-500">Ventana:</span>
                  <p className="font-medium">
                    {showInvoiceDetail.window === 1 ? 'Corrida día 15' : 'Corrida fin de mes'}
                  </p>
                </div>
              </div>

              <div className="border-t pt-4">
                <p className="text-slate-500 text-sm mb-1">Concepto:</p>
                <p className="font-medium">{showInvoiceDetail.concepto}</p>
              </div>

              <div className="grid grid-cols-3 gap-4 border-t pt-4">
                <div className="text-center">
                  <p className="text-slate-500 text-xs">Subtotal</p>
                  <p className="font-medium">{formatCurrency(showInvoiceDetail.monto_subtotal)}</p>
                </div>
                <div className="text-center">
                  <p className="text-slate-500 text-xs">IVA</p>
                  <p className="font-medium">{formatCurrency(showInvoiceDetail.monto_iva)}</p>
                </div>
                <div className="text-center">
                  <p className="text-slate-500 text-xs">Total</p>
                  <p className="font-bold text-lg">{formatCurrency(showInvoiceDetail.monto_total)}</p>
                </div>
              </div>

              {/* Files */}
              <div className="border-t pt-4 flex gap-4">
                {showInvoiceDetail.pdf_url && (
                  <a
                    href={showInvoiceDetail.pdf_url}
                    download={`factura_${showInvoiceDetail.numero_factura}.pdf`}
                    className="flex items-center gap-2 text-blue-600 hover:underline text-sm"
                  >
                    <Download size={14} />
                    Descargar PDF
                  </a>
                )}
                {showInvoiceDetail.xml_url && (
                  <a
                    href={showInvoiceDetail.xml_url}
                    download={`factura_${showInvoiceDetail.numero_factura}.xml`}
                    className="flex items-center gap-2 text-blue-600 hover:underline text-sm"
                  >
                    <Download size={14} />
                    Descargar XML
                  </a>
                )}
              </div>

              {/* Status Update */}
              <div className="border-t pt-4 space-y-3">
                <Label>Actualizar Estado</Label>
                <Select
                  value={invoiceStatusUpdate.status}
                  onValueChange={(v) => setInvoiceStatusUpdate(prev => ({ ...prev, status: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recibida">Recibida</SelectItem>
                    <SelectItem value="en_revision">En Revisión</SelectItem>
                    <SelectItem value="autorizada">Autorizada</SelectItem>
                    <SelectItem value="rechazada">Rechazada</SelectItem>
                    <SelectItem value="pagada">Pagada</SelectItem>
                  </SelectContent>
                </Select>

                <Textarea
                  value={invoiceStatusUpdate.comentarios}
                  onChange={(e) => setInvoiceStatusUpdate(prev => ({ ...prev, comentarios: e.target.value }))}
                  placeholder="Comentarios (requerido si rechaza)..."
                />

                <div className="flex justify-end">
                  <Button
                    onClick={handleUpdateInvoiceStatus}
                    disabled={submitting || invoiceStatusUpdate.status === showInvoiceDetail.status}
                    className="bg-slate-900 hover:bg-slate-800"
                  >
                    {submitting && <Loader2 className="animate-spin mr-2" size={16} />}
                    Actualizar Estado
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Logs Dialog */}
      <Dialog open={!!showLogs} onOpenChange={() => setShowLogs(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Historial del Proveedor</DialogTitle>
          </DialogHeader>
          {showLogs && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded">
                <p className="font-medium">{showLogs.razon_social}</p>
              </div>

              <div className="max-h-80 overflow-y-auto space-y-3">
                {supplierLogs.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">Sin registros</p>
                ) : (
                  supplierLogs.map(log => (
                    <div key={log.id} className="border-l-2 border-slate-200 pl-3 py-1">
                      <p className="text-sm font-medium text-slate-900">{log.action}</p>
                      <p className="text-sm text-slate-600">{log.details}</p>
                      <p className="text-xs text-slate-400">{formatDateTime(log.created_at)}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Proveedores;
